import Policies from '@/core/policies';

import {
  getRecentOrders,
  updateRecentOrder,
  recentOrderValidity,
  removeRecentOrders,
} from '@/repositories/Orders';

// selectedOrderActivated is used because whenever the user leaves the cart, the selected order gets deselected.
// If a different type of order is chosen from the selection modal, it triggers exiting and re-entering the cart.
// With this flag, we ensure that the selected order is not set to null before the new cart finishes loading.
export default {
  namespaced: true,
  state: {
    orders: [],
    selectedOrder: null,
    selectedOrderActivated: null,
    loading: false,
    mutuating: false,
    error: '',
    mutuationError: '',
  },

  getters: {
    recentOrders: (state) => state.orders,
    unprocessedRecentOrders: (state) =>
      state.orders.filter((o) => !o.processed),
    processedRecentOrders: (state) => state.orders.filter((o) => o.processed),
    selectedRecentOrder: (state) => state.selectedOrder,
    isRecentOrderSelected: (state) => state.selectedOrder !== null,
    isRecentOrderSelectedActivated: (state) => state.selectedOrderActivated,
    recentOrdersCount: (state) => state.orders.length,
    unprocessedRecentOrdersCount: (state, getters) =>
      getters.unprocessedRecentOrders.length,
    hasRecentOrders: (state) => state.orders.length > 0,
    hasUnprocessedRecentOrders: (state, getters) =>
      getters.unprocessedRecentOrdersCount > 0,
    loadingRecentOrders: (state) => state.loading,
    removingOrder: (state) => state.removing,
    mutuatingRecentOrders: (state) => state.mutuating,
    recentOrdersError: (state) => state.error,
    recentOrdersMutuationError: (state) => state.mutuationError,
  },

  mutations: {
    SET_ORDERS(state, orders) {
      state.orders = orders;
    },

    SET_SELECTED_ORDER(state, orderId) {
      state.selectedOrderActivated = false;

      if (orderId !== null) {
        const orderIndex = state.orders.findIndex(({ id }) => id === orderId);

        if (orderIndex !== -1) {
          state.selectedOrder = state.orders[orderIndex];
        }
      } else {
        state.selectedOrder = null;
      }
    },

    RESET_STORE(state) {
      state.orders = [];
      state.selectedOrder = null;
      state.error = '';
    },

    REMOVE_ORDERS(state, ids) {
      const updatedOrders = state.orders.filter(({ id }) => !ids.includes(id));
      state.orders = updatedOrders;
    },

    UPDATE_ORDER(state, { orderId, newData }) {
      const orderIndex = state.orders.findIndex(({ id }) => id === orderId);

      if (orderIndex !== -1) {
        state.orders.splice(orderIndex, 1, newData);
      }

      if (state.selectedOrder?.id === newData.id) {
        state.selectedOrder = newData;
      }
    },

    UPDATE_ORDER_COUNT(state, { orderId, newCount }) {
      const orderIndex = state.orders.findIndex(({ id }) => id === orderId);

      if (orderIndex !== -1) {
        const order = state.orders[orderIndex];
        order.materialsCount = newCount;
        state.orders.splice(orderIndex, 1, order);
      }
    },

    SET_LOADING(state, status) {
      state.loading = status;
    },

    SET_MUTUATING(state, status) {
      state.mutuating = status;
    },
    SET_ERROR(state, msg) {
      state.error = msg;
    },
    SET_MUTUATION_ERROR(state, msg) {
      state.mutuationError = msg;
    },
  },

  actions: {
    async loadRecentOrders({ rootGetters, commit }) {
      commit('SET_ERROR', '');

      const userCode = rootGetters['auth/userCode'];
      const role = rootGetters['auth/mainRole'].name;
      const channel = rootGetters['auth/channel'];

      if (userCode && channel && Policies.OrderPolicy.canList()) {
        commit('SET_LOADING', true);

        try {
          const orders = await getRecentOrders(userCode, role, channel);
          commit('SET_ORDERS', orders);
        } catch (error) {
          commit('SET_ERROR', 'No se pudo obtener la lista de "Carritos"');
        }

        commit('SET_LOADING', false);
      } else {
        commit('RESET_STORE');
      }
    },

    setSelectedOrderAsActivated({ state }) {
      state.selectedOrderActivated = true;
    },

    async updateRecentOrder(
      { commit, rootGetters },
      order = {
        id: null,
        newData: { total: null, subtotal: null, tax: null, name: null },
      }
    ) {
      commit('SET_MUTUATING', true);
      commit('SET_MUTUATION_ERROR', '');
      const customer = rootGetters['selectedUser/customer'];
      const userId = rootGetters['auth/userId'];
      const role = rootGetters['auth/mainRole'];

      try {
        const newData = await updateRecentOrder(
          userId,
          role.name,
          customer.customer,
          customer.VTWEG,
          order.id,
          order.newData
        );
        commit('UPDATE_ORDER', { orderId: order.id, newData });
      } catch (error) {
        const { response } = error;

        if (response.status === 404) {
          commit('REMOVE_ORDERS', [order.id]);
        } else if (response.status === 409) {
          commit('SET_MUTUATION_ERROR', response?.data?.message);
        } else {
          commit('SET_MUTUATION_ERROR', 'No fue posible actualizar la orden');
        }
      }

      commit('SET_MUTUATING', false);
    },

    async updateRecentOrderItemsCount({ commit }, { orderId, newCount }) {
      commit('UPDATE_ORDER_COUNT', { orderId, newCount });
    },

    async deleteRecentOrders({ rootGetters, commit, dispatch }, orderIds) {
      commit('SET_MUTUATING', true);
      commit('SET_MUTUATION_ERROR', '');

      const userId = rootGetters['auth/userId'];
      const channel = rootGetters['auth/channel'];

      try {
        await removeRecentOrders(userId, channel, orderIds);
        dispatch('loadRecentOrders');
        commit('REMOVE_ORDERS', orderIds);
      } catch (error) {
        commit('SET_MUTUATION_ERROR', 'No fue posible eliminar las órdenes');
      }

      commit('SET_MUTUATING', false);
    },

    cleanRecentOrders({ commit }) {
      commit('RESET_STORE');
    },

    async setRecentOrderSelected(
      { commit, dispatch, state, rootGetters },
      orderId
    ) {
      const order = state.orders.find((o) => o.id === orderId);

      if (!orderId || !order) {
        commit('SET_SELECTED_ORDER', null);
        return;
      }

      const channel = rootGetters['auth/channel'];
      commit('SET_MUTUATING', true);
      commit('SET_MUTUATION_ERROR', '');

      const orderStatus = await recentOrderValidity(orderId, channel);

      if (!orderStatus.valid) {
        commit('SET_MUTUATING', false);
        commit('SET_MUTUATION_ERROR', orderStatus.message);
        commit('REMOVE_ORDERS', [orderId]);
        commit('SET_SELECTED_ORDER', null);
        return;
      }

      await dispatch(
        'selectedUser/setSelectedCustomerCode',
        order.customerCode,
        {
          root: true,
        }
      );

      await dispatch('selectedUser/loadCustomer', null, {
        root: true,
      });

      if (rootGetters['selectedUser/errorOnLoad']) {
        commit('SET_SELECTED_ORDER', null);
        commit('SET_MUTUATION_ERROR', 'No se pudo seleccionar el carrito');
      } else {
        commit('SET_SELECTED_ORDER', orderId);
      }

      commit('SET_MUTUATING', false);
    },
  },
};
